import { Checkbox } from '@/components_deprecated'
import { Module } from '@/modules/companies/consts'
import { DynamicTable } from '@/modules/dynamic_tables'
import { ALL_ISSUE_RELATIONS_QUERY } from '@/modules/issues'
import { useOnErrorAuto } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { PATCH_ISSUE_RELATION_MUTATION } from '@/modules/issues'
import { DynamicTableSettings } from '../TableSettings'
import { OldSection, SectionHeader } from '../common'

interface RelationNode {
  id: string
  name: string
  visibleInTable: boolean
}

interface AllIssueRelationsData {
  allIssueRelations: { edges: { node: RelationNode }[] }
}

/**
 * The issue table's columns.
 *
 * Two blocks because two things back them: the built-in columns are
 * DynamicColumn rows, shared with the activities and salary tables, while a
 * relation column is driven from the relation itself — a DynamicColumn keys on
 * a static name, so a row per relation would need syncing on every rename.
 */
export const IssueTableSettingsSection: React.FC = () => {
  const translations = useTranslate({
    header: 'settings.issue-table',
    info: 'settings.info.issue-table',
    relations: 'settings.issue-table-relations',
    noRelations: 'settings.issue-table-no-relations',
  })

  const onErrorAuto = useOnErrorAuto()

  const { data } = useQuery<AllIssueRelationsData>(ALL_ISSUE_RELATIONS_QUERY, {
    onError: onErrorAuto(),
  })

  const [patchRelation, { loading }] = useMutation(
    PATCH_ISSUE_RELATION_MUTATION,
    {
      refetchQueries: ['AllIssueRelations'],
      onError: onErrorAuto(),
    }
  )

  const relations = (data?.allIssueRelations.edges ?? []).map(edge => edge.node)

  return (
    <OldSection>
      <SectionHeader>{translations.header}</SectionHeader>
      <p className="mb-4 max-w-3xl text-sm text-stone-500">
        {translations.info}
      </p>

      <DynamicTableSettings
        name={Module.ISSUES}
        table={DynamicTable.ISSUES}
        minEnabledColumns={1}
      />

      <div className="mt-8">
        <h4 className="mb-3 text-sm font-semibold text-stone-900">
          {translations.relations}
        </h4>

        {relations.length === 0 ? (
          <p className="text-sm text-stone-500">{translations.noRelations}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {relations.map(relation => (
              <li key={relation.id} className="flex items-center gap-2">
                <Checkbox
                  checked={relation.visibleInTable}
                  disabled={loading}
                  label={relation.name}
                  onChange={visibleInTable =>
                    void patchRelation({
                      variables: {
                        id: relation.id,
                        input: { visibleInTable },
                      },
                    })
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </OldSection>
  )
}
